import { Box, HStack } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useId } from "react";

import {
  ADVISOR_KIN_MINT_100,
  ADVISOR_KIN_MINT_200,
  ADVISOR_KIN_MINT_400,
  ADVISOR_PANEL_PRIMARY_TEXT,
} from "./advisor-panel-tokens";

export type KinIconProps = {
  size?: string | number;
  width?: string | number;
  height?: string | number;
  className?: string;
  role?: string;
  title?: string;
  focusable?: boolean | "true" | "false";
  "aria-hidden"?: boolean | "true" | "false";
  "aria-label"?: string;
  "data-testid"?: string;
};

function resolveIconSize(
  size: KinIconProps["size"],
  fallback: string | number,
) {
  return size ?? fallback;
}

export function KinAgentMessageIcon({
  size,
  width,
  height,
  ...props
}: KinIconProps) {
  const clipId = `kin-agent-message-icon-${useId().replace(/:/g, "")}`;
  const resolvedWidth = width ?? resolveIconSize(size, 32);
  const resolvedHeight = height ?? resolveIconSize(size, 32);

  return (
    <svg
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox="0 0 32 32"
      fill="none"
      {...props}
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M16 0.5C24.5604 0.5 31.5 7.43959 31.5 16V16.3477C31.5 24.716 24.716 31.5 16.3477 31.5H15.6523C7.28403 31.5 0.5 24.716 0.5 16.3477V16C0.5 7.43959 7.43959 0.5 16 0.5Z"
          fill="white"
        />
        <path
          d="M16 0.5C24.5604 0.5 31.5 7.43959 31.5 16V16.3477C31.5 24.716 24.716 31.5 16.3477 31.5H15.6523C7.28403 31.5 0.5 24.716 0.5 16.3477V16C0.5 7.43959 7.43959 0.5 16 0.5Z"
          stroke="#E7E7E7"
        />
        <path
          d="M9.34638 9.51953L22.6451 9.51953C23.391 9.51953 23.9957 10.1243 23.9957 10.8702L23.9957 19.8052C23.9957 20.5512 23.391 21.1559 22.6451 21.1559H15.5801L11.8399 24.4806L11.8399 21.1559H9.34638C8.60046 21.1559 7.99573 20.5512 7.99573 19.8052L7.99573 10.8702C7.99573 10.1243 8.60045 9.51953 9.34638 9.51953Z"
          fill="white"
          fillOpacity="0.03"
          style={{ stroke: ADVISOR_PANEL_PRIMARY_TEXT }}
          strokeLinejoin="round"
        />
        <path
          d="M16.0602 11.665C18.1526 11.6114 19.5804 13.069 19.943 14.5674C20.233 15.7582 20.0025 16.8668 19.2614 17.8398C18.6692 18.6213 17.8613 19.0992 16.9088 19.2744C15.8453 19.4711 14.8461 19.2555 13.9674 18.6035C13.0415 17.9179 12.4869 16.9889 12.3971 15.8379C12.2178 13.5673 13.8737 11.8009 16.0573 11.665H16.0602ZM14.194 15.8428C14.1661 15.975 14.1388 16.1093 14.1071 16.2432L14.1061 16.2441C14.0431 16.5029 13.9769 16.762 13.9069 17.0176C13.845 17.2454 13.7756 17.4728 13.7076 17.6992C13.7126 17.7047 13.718 17.7105 13.7233 17.7168C14.3337 18.3977 15.2232 18.8242 16.2067 18.8242C16.2784 18.8242 16.3473 18.8207 16.4186 18.8154C16.4142 18.7708 16.4115 18.7243 16.4137 18.6787V18.5459C16.3978 18.2904 16.288 18.0748 16.1168 17.875L16.026 17.7764C15.9263 17.6734 15.8224 17.5759 15.7164 17.4736C15.6116 17.3724 15.5067 17.2673 15.4108 17.1533C15.1281 16.8187 14.8623 16.4588 14.5914 16.123V16.1221C14.4738 15.9805 14.3633 15.8709 14.2047 15.8369H14.2037C14.2011 15.8363 14.1988 15.8362 14.1969 15.8359C14.1962 15.8379 14.1948 15.8401 14.194 15.8428ZM17.778 18.1748C17.6736 18.1934 17.5639 18.1975 17.4713 18.2041H17.4703C17.271 18.2181 17.0772 18.2278 16.8824 18.248C16.8804 18.2491 16.8782 18.2511 16.8756 18.2529C16.86 18.3594 16.8512 18.4674 16.8405 18.5801V18.7354L16.8424 18.7578C17.3198 18.6672 17.7605 18.4712 18.1422 18.1992C18.0212 18.1668 17.9029 18.1523 17.778 18.1748ZM16.6823 14.3301C16.5911 14.3616 16.4979 14.3829 16.4098 14.4033C16.3201 14.4241 16.2354 14.4424 16.153 14.4688L16.1539 14.4697C15.7604 14.5996 15.3704 14.7202 14.9938 14.8721H14.9928C14.7566 14.9671 14.6238 15.1645 14.5807 15.4658C14.5903 15.4862 14.6004 15.5073 14.6071 15.5215C14.6212 15.5514 14.635 15.5756 14.65 15.5928L14.651 15.5938L14.9655 15.9482C15.0719 16.0664 15.1796 16.186 15.2838 16.3096C15.3832 16.4284 15.4767 16.5536 15.569 16.6729C15.6621 16.7932 15.7557 16.9096 15.8571 17.0176C16.0984 17.2758 16.3685 17.508 16.6344 17.7559C16.7054 17.8211 16.7757 17.843 16.861 17.8193H16.8629C16.888 17.8131 16.9123 17.812 16.9283 17.8115C16.9475 17.811 16.957 17.8107 16.9674 17.8096H16.9713C17.2279 17.792 17.4767 17.7781 17.7262 17.7539C18.0061 17.727 18.2785 17.7625 18.5289 17.8818C18.5312 17.8797 18.5335 17.8783 18.5348 17.877L18.5358 17.876C19.16 17.2694 19.5465 16.4222 19.5465 15.498C19.5465 15.2219 19.5092 14.9518 19.4459 14.6953C19.0888 14.9205 18.7034 14.9551 18.3092 14.8262C17.8515 14.6761 17.3975 14.514 16.9459 14.3613L16.944 14.3604C16.838 14.3216 16.7676 14.3008 16.6823 14.3301ZM13.1881 14.0615C12.9807 14.4931 12.8658 14.9777 12.8658 15.4883C12.8659 16.1452 13.0617 16.7592 13.3932 17.2773C13.4192 17.1898 13.4458 17.1038 13.4694 17.0176C13.5462 16.7316 13.6222 16.4433 13.6881 16.1592C13.7388 15.9431 13.7779 15.7236 13.8258 15.4932C13.8152 15.4444 13.806 15.3978 13.7955 15.3525L13.7535 15.1973C13.7207 15.0937 13.6859 14.9906 13.651 14.8867C13.6161 14.7831 13.581 14.6779 13.5475 14.5723C13.5336 14.5285 13.5244 14.4803 13.5162 14.4443C13.5075 14.4057 13.4989 14.3752 13.4869 14.3496L13.4176 14.2461C13.3571 14.174 13.2782 14.1161 13.1881 14.0615ZM16.0563 12.1572C15.497 12.181 14.9713 12.3449 14.5172 12.6123L14.5123 12.6162L14.4772 12.6367L14.4733 12.6396L14.4215 12.5537L14.4723 12.6396C14.2625 12.7647 14.0736 12.9101 13.9049 13.0752L13.9039 13.0762C13.7117 13.2581 13.541 13.4633 13.3951 13.6865C13.6029 13.8285 13.8081 13.9982 13.9108 14.2656L13.941 14.3584C13.9493 14.3889 13.9563 14.4191 13.9625 14.4482C13.9756 14.5094 13.9843 14.5617 13.9977 14.6152L13.9987 14.6182C14.0171 14.6976 14.0983 14.7552 14.1608 14.75H14.1617C14.2787 14.7405 14.3835 14.7125 14.4879 14.6543L14.6471 14.5684C15.0234 14.3762 15.4222 14.2416 15.8209 14.1221L16.2047 14.0156C16.3328 13.9826 16.4608 13.9516 16.5865 13.9189C16.6108 13.9125 16.6188 13.9062 16.6207 13.9043C16.6217 13.9033 16.6251 13.8984 16.6276 13.8818C16.6478 13.7335 16.6684 13.5882 16.6949 13.4053C16.6827 13.3478 16.6718 13.2741 16.6608 13.2188C16.6469 13.1493 16.6293 13.0855 16.6022 13.0312V13.0303C16.4497 12.7325 16.2772 12.4438 16.1002 12.1562C16.0869 12.1569 16.072 12.1571 16.0563 12.1572ZM16.8629 12.5996C16.9171 12.6977 16.9792 12.8031 17.0328 12.9102C17.0388 12.922 17.0451 12.9342 17.0533 12.9492C17.0612 12.9635 17.0706 12.9806 17.0787 12.998C17.0939 13.0303 17.1112 13.0761 17.109 13.1279C17.1019 13.3716 17.0849 13.6124 17.0602 13.8516V13.8525C17.0568 13.8814 17.055 13.9004 17.0553 13.9141C17.0556 13.9269 17.0572 13.9297 17.0563 13.9277C17.052 13.9189 17.0478 13.9278 17.1002 13.9453H17.1022C17.3355 14.0259 17.5655 14.108 17.7955 14.1875L18.4889 14.415L18.609 14.4453C18.8636 14.4915 19.0986 14.4257 19.3112 14.2656C18.8731 13.1525 17.8518 12.3351 16.6246 12.1807C16.7046 12.3198 16.785 12.4589 16.8629 12.5996Z"
          style={{ fill: ADVISOR_PANEL_PRIMARY_TEXT, stroke: ADVISOR_PANEL_PRIMARY_TEXT }}
          strokeWidth="0.2"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function KinCloseIcon({ size, width, height, ...props }: KinIconProps) {
  const resolvedWidth = width ?? resolveIconSize(size, 16);
  const resolvedHeight = height ?? resolveIconSize(size, 16);

  return (
    <svg
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox="0 0 16 16"
      fill="none"
      {...props}
    >
      <path
        d="M4.26664 12.4359L3.56409 11.7333L7.29742 8L3.56409 4.26667L4.26664 3.56412L7.99997 7.29745L11.7333 3.56412L12.4359 4.26667L8.70252 8L12.4359 11.7333L11.7333 12.4359L7.99997 8.70255L4.26664 12.4359Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function KinHistoryIcon({
  size,
  width,
  height,
  ...props
}: KinIconProps) {
  const resolvedWidth = width ?? resolveIconSize(size, 18);
  const resolvedHeight = height ?? resolveIconSize(size, 18);

  return (
    <svg
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox="0 0 18 18"
      fill="none"
      {...props}
    >
      <path
        d="M8.9856 15.375C7.36156 15.375 5.94618 14.8404 4.73945 13.7711C3.53273 12.7019 2.83802 11.3615 2.65533 9.75H3.80338C3.9928 11.0452 4.57573 12.119 5.55217 12.9714C6.52862 13.8238 7.6731 14.25 8.9856 14.25C10.4481 14.25 11.6887 13.7406 12.7075 12.7219C13.7262 11.7031 14.2356 10.4625 14.2356 9C14.2356 7.5375 13.7262 6.29687 12.7075 5.27812C11.6887 4.25937 10.4481 3.75 8.9856 3.75C8.16637 3.75 7.39858 3.93197 6.68223 4.2959C5.9659 4.65984 5.34907 5.16056 4.83176 5.79806H6.79329V6.92304H2.98565V3.11538H4.11062V4.89232C4.71927 4.17406 5.44811 3.61661 6.29715 3.21997C7.14618 2.82333 8.04233 2.62502 8.9856 2.62502C9.87021 2.62502 10.6991 2.79233 11.4721 3.12695C12.2452 3.46157 12.9192 3.91637 13.4942 4.49135C14.0692 5.06635 14.524 5.74039 14.8586 6.51347C15.1933 7.28654 15.3606 8.11538 15.3606 9C15.3606 9.88461 15.1933 10.7135 14.8586 11.4865C14.524 12.2596 14.0692 12.9336 13.4942 13.5086C12.9192 14.0836 12.2452 14.5384 11.4721 14.873C10.6991 15.2077 9.87021 15.375 8.9856 15.375ZM11.237 12.0202L8.44473 9.22788V5.25H9.56972V8.77211L12.0274 11.2298L11.237 12.0202Z"
        fill="#3D3D3D"
      />
    </svg>
  );
}

export function KinNewChatIcon({
  size,
  width,
  height,
  ...props
}: KinIconProps) {
  const resolvedWidth = width ?? resolveIconSize(size, 18);
  const resolvedHeight = height ?? resolveIconSize(size, 18);

  return (
    <svg
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox="0 0 18 18"
      fill="none"
      {...props}
    >
      <path
        d="M3.98081 15.375C3.60194 15.375 3.28125 15.2438 3.01875 14.9813C2.75625 14.7188 2.625 14.3981 2.625 14.0192V3.98081C2.625 3.60194 2.75625 3.28125 3.01875 3.01875C3.28125 2.75625 3.60194 2.625 3.98081 2.625H10.4726L9.34763 3.75H3.98081C3.92306 3.75 3.87019 3.77406 3.82219 3.82219C3.77406 3.87019 3.75 3.92306 3.75 3.98081V14.0192C3.75 14.0769 3.77406 14.1298 3.82219 14.1778C3.87019 14.2259 3.92306 14.25 3.98081 14.25H14.0192C14.0769 14.25 14.1298 14.2259 14.1778 14.1778C14.2259 14.1298 14.25 14.0769 14.25 14.0192V8.60475L15.375 7.47975V14.0192C15.375 14.3981 15.2438 14.7188 14.9813 14.9813C14.7188 15.2438 14.3981 15.375 14.0192 15.375H3.98081ZM7.125 10.875V8.30044L13.9198 1.50581C14.0361 1.38944 14.1639 1.30456 14.3034 1.25119C14.4428 1.19781 14.5846 1.17113 14.7289 1.17113C14.876 1.17113 15.0173 1.19781 15.1528 1.25119C15.2884 1.30456 15.412 1.387 15.5235 1.4985L16.4653 2.4375C16.5739 2.55388 16.6574 2.68225 16.7156 2.82263C16.7738 2.963 16.8028 3.10531 16.8028 3.24956C16.8028 3.39381 16.7781 3.53369 16.7286 3.66919C16.6791 3.80481 16.5961 3.93075 16.4798 4.047L9.66338 10.875H7.125ZM8.25 9.75H9.18469L13.8591 5.07544L13.3918 4.60819L12.8914 4.1265L8.25 8.76788V9.75Z"
        fill="#3D3D3D"
      />
    </svg>
  );
}

export function KinSendIcon({ size, width, height, ...props }: KinIconProps) {
  const resolvedWidth = width ?? resolveIconSize(size, 24);
  const resolvedHeight = height ?? resolveIconSize(size, 24);

  return (
    <svg
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox="0 0 24 24"
      fill="none"
      {...props}
    >
      <path
        d="M3.5 19.25V4.75L20.7115 12L3.5 19.25ZM5 17L16.85 12L5 7V10.6923L10.423 12L5 13.3078V17Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Kin Chatbot message feedback — thumbs from design export (``18×18``); use ``currentColor`` for idle/selected/hover. */
export function KinThumbUpIcon({
  size,
  width,
  height,
  ...props
}: KinIconProps) {
  const resolvedWidth = width ?? resolveIconSize(size, 18);
  const resolvedHeight = height ?? resolveIconSize(size, 18);

  return (
    <svg
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox="0 0 18 18"
      fill="none"
      {...props}
    >
      <path
        d="M13.2981 15.3747H5.40862V6.37473L10.3847 1.42773L11.0192 2.06223C11.0971 2.14011 11.1617 2.24323 11.2133 2.37161C11.2646 2.49998 11.2903 2.62092 11.2903 2.73442V2.92473L10.4942 6.37473H15.5192C15.8759 6.37473 16.1911 6.51155 16.4646 6.78517C16.7382 7.05867 16.875 7.3738 16.875 7.73055V8.94199C16.875 9.01986 16.8649 9.10399 16.8446 9.19436C16.8245 9.28474 16.8019 9.36892 16.7769 9.44692L14.6278 14.518C14.5202 14.7584 14.3399 14.9613 14.0871 15.1267C13.8342 15.292 13.5712 15.3747 13.2981 15.3747ZM6.53363 14.2497H13.2981C13.3509 14.2497 13.405 14.2353 13.4603 14.2064C13.5156 14.1775 13.5578 14.1295 13.5866 14.0622L15.75 8.99974V7.73055C15.75 7.66317 15.7284 7.60786 15.6851 7.56461C15.6419 7.52136 15.5866 7.49974 15.5192 7.49974H9.07219L10.0125 3.38917L6.53363 6.85361V14.2497ZM5.40862 6.37473V7.49974H3V14.2497H5.40862V15.3747H1.875V6.37473H5.40862Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function KinThumbDownIcon({
  size,
  width,
  height,
  ...props
}: KinIconProps) {
  const resolvedWidth = width ?? resolveIconSize(size, 18);
  const resolvedHeight = height ?? resolveIconSize(size, 18);

  return (
    <svg
      width={resolvedWidth}
      height={resolvedHeight}
      viewBox="0 0 18 18"
      fill="none"
      {...props}
    >
      <path
        d="M4.70194 2.92773H12.5914V11.9275L7.61531 16.8747L6.98081 16.24C6.90294 16.1622 6.83825 16.059 6.78675 15.9307C6.73537 15.8023 6.70969 15.6814 6.70969 15.568V15.3775L7.50581 11.9275H2.48081C2.12406 11.9275 1.80894 11.7908 1.53544 11.5173C1.26181 11.2437 1.125 10.9285 1.125 10.5719V9.3603C1.125 9.28242 1.13512 9.1983 1.15537 9.10792C1.1755 9.01755 1.19806 8.93342 1.22306 8.85555L3.37219 3.78442C3.47981 3.54405 3.66006 3.34117 3.91294 3.1758C4.16581 3.01042 4.42881 2.92773 4.70194 2.92773ZM11.4664 4.05273H4.70194C4.64906 4.05273 4.595 4.06711 4.53975 4.09586C4.48438 4.12474 4.44225 4.17286 4.41338 4.24023L2.25 9.30274V10.5719C2.25 10.6392 2.27163 10.6944 2.31488 10.7377C2.35812 10.781 2.41344 10.8027 2.48081 10.8027H8.92781L7.9875 14.9133L11.4664 11.4489V4.05273ZM12.5914 11.9275V10.8027H15V4.05273H12.5914V2.92773H16.125V11.9275H12.5914Z"
        fill="currentColor"
      />
    </svg>
  );
}

const kinThinkingDotPulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.56;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-5px);
  }
`;

const KIN_THINKING_DOT_COLORS = [
  ADVISOR_KIN_MINT_400,
  ADVISOR_KIN_MINT_200,
  ADVISOR_KIN_MINT_100,
] as const;

/** Animated dots only — for inline use beside an existing Kin avatar (e.g. assistant bubble column). */
export function KinThinkingDots() {
  return (
    <HStack gap="6px" minH="32px" align="center" aria-hidden>
      {KIN_THINKING_DOT_COLORS.map((color, index) => (
        <Box
          key={`${color}-${index}`}
          data-testid="kin-thinking-dot"
          boxSize="7px"
          borderRadius="full"
          bg={color}
          css={{
            animation: `${kinThinkingDotPulse} 1.35s ease-in-out ${index * 0.2}s infinite both`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              opacity: 0.72,
              transform: "none",
            },
          }}
        />
      ))}
    </HStack>
  );
}

export function KinThinkingIndicator() {
  return (
    <HStack
      align="start"
      gap="11px"
      alignSelf="stretch"
      w="100%"
      aria-busy="true"
      aria-label="Assistant is preparing a reply"
    >
      <Box flexShrink={0} mt={0.5}>
        <KinAgentMessageIcon aria-hidden focusable={false} />
      </Box>
      <KinThinkingDots />
    </HStack>
  );
}
